import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff, Download, FileSpreadsheet, CheckCircle2, Clock3, Flashlight, Loader2, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";

const statusStyles = {
  '-': "bg-slate-100 text-slate-700 border border-slate-200",
  Staging: "bg-amber-100 text-amber-700 border border-amber-200",
  Done: "bg-emerald-100 text-emerald-700 border border-emerald-200",
};

export default function AdminStagingPage() {
  const [serialNumber, setSerialNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rows, setRows] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const scannerRef = useRef(null);
  const scanAudioContextRef = useRef(null);
  const cameraAutoSubmitRef = useRef(false);
  const lastScanRef = useRef({ value: "", timestamp: 0 });
  const processingSerialRef = useRef(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [autoSubmitScan, setAutoSubmitScan] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);

  const fetchData = async () => {
    try {
      setFetching(true);
      const response = await api.get("/staging");
      setRows(response.data.data || []);
    } catch (error) {
      toast.error(apiError(error.response?.data));
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const prepareScanAudio = () => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    if (!scanAudioContextRef.current) {
      scanAudioContextRef.current = new AudioContextClass();
    }
    if (scanAudioContextRef.current.state === "suspended") {
      scanAudioContextRef.current.resume().catch(() => {});
    }
  };

  const playScanSound = () => {
    const audioContext = scanAudioContextRef.current;
    if (!audioContext || audioContext.state !== "running") return;

    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
    gain.gain.setValueAtTime(0.18, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.16);
    oscillator.connect(gain);
    gain.connect(audioContext.destination);
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.16);
  };

  const stopCamera = async () => {
    if (!scannerRef.current || !scannerActive) {
      setScannerActive(false);
      setTorchEnabled(false);
      setTorchSupported(false);
      return;
    }

    try {
      await scannerRef.current.stop();
    } catch (error) {
      console.warn("Unable to stop camera scanner", error);
    } finally {
      setScannerActive(false);
      setTorchEnabled(false);
      setTorchSupported(false);
    }
  };

  const startCamera = async (requestedFacingMode = facingMode) => {
    if (scannerActive || scannerLoading) return;

    prepareScanAudio();
    setScannerLoading(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode("staging-camera-reader");
      }

      await scannerRef.current.start(
        { facingMode: requestedFacingMode },
        {
          fps: 12,
          qrbox: { width: 260, height: 120 },
          aspectRatio: 1.333333,
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        },
        (decodedText) => {
          const now = Date.now();
          if (lastScanRef.current.value === decodedText && now - lastScanRef.current.timestamp < 1500) return;

          lastScanRef.current = { value: decodedText, timestamp: now };
          playScanSound();
          setSerialNumber(decodedText);
          if (cameraAutoSubmitRef.current) processSerial(decodedText);
        },
        () => {},
      );

      setScannerActive(true);
      try {
        setTorchSupported(Boolean(scannerRef.current.getRunningTrackCapabilities()?.torch));
      } catch (error) {
        setTorchSupported(false);
      }
    } catch (error) {
      const insecureLocalhost = !window.isSecureContext && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1";
      toast.error(
        insecureLocalhost
          ? "Kamera diblokir browser. Buka aplikasi melalui http://localhost:3000, atau gunakan HTTPS saat mengakses dari IP/LAN."
          : "Kamera tidak dapat dibuka. Pastikan izin kamera diberikan pada browser."
      );
    } finally {
      setScannerLoading(false);
    }
  };

  const switchCamera = async () => {
    if (!scannerActive) return;
    const nextFacingMode = facingMode === "environment" ? "user" : "environment";
    await stopCamera();
    setFacingMode(nextFacingMode);
    await startCamera(nextFacingMode);
  };

  const toggleTorch = async () => {
    if (!scannerActive || !torchSupported) return;

    const nextTorchState = !torchEnabled;
    try {
      await scannerRef.current.applyVideoConstraints({ advanced: [{ torch: nextTorchState }] });
      setTorchEnabled(nextTorchState);
    } catch (error) {
      toast.error("Flash kamera tidak tersedia pada perangkat ini.");
    }
  };

  useEffect(() => () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
    }
  }, []);

  const processSerial = async (value = serialNumber) => {
    const trimmedSerial = value.trim();
    if (!trimmedSerial || processingSerialRef.current) return;

    processingSerialRef.current = true;
    setLoading(true);
    try {
      const response = await api.post("/staging/process-serial", {
        serial_number: trimmedSerial,
      });
      toast.success(response.data.message);
      setSerialNumber("");
      await fetchData();
    } catch (error) {
      toast.error(apiError(error.response?.data));
    } finally {
      processingSerialRef.current = false;
      setLoading(false);
    }
  };

  const handleSerialSubmit = (event) => {
    event.preventDefault();
    processSerial();
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const response = await api.post("/staging/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(response.data.message);
      await fetchData();
    } catch (error) {
      toast.error(apiError(error.response?.data));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      await fetchData();
      return;
    }

    try {
      setFetching(true);
      const response = await api.get("/staging/search", {
        params: { serial_number: searchValue.trim() },
      });
      setRows(response.data.data || []);
    } catch (error) {
      toast.error(apiError(error.response?.data));
    } finally {
      setFetching(false);
    }
  };

  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter((row) => row.status === statusFilter);
  }, [rows, statusFilter]);

  const summary = useMemo(() => {
    const staging = rows.filter((row) => row.status === "Staging").length;
    const done = rows.filter((row) => row.status === "Done").length;
    const pending = rows.filter((row) => row.status === "-").length;
    return { staging, done, pending };
  }, [rows]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-heading text-4xl font-black tracking-tighter">Staging</h1>
            <p className="mt-1 text-sm text-muted-foreground">Upload Excel/CSV atau input serial number satu per satu.</p>
          </div>
          <a href="/staging-template.csv" download className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground hover:bg-secondary">
            <Download className="h-4 w-4" /> Download Template
          </a>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Pending</span>
              <Clock3 className="h-4 w-4" />
            </div>
            <div className="mt-3 text-3xl font-bold">{summary.pending}</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Staging</span>
              <FileSpreadsheet className="h-4 w-4" />
            </div>
            <div className="mt-3 text-3xl font-bold">{summary.staging}</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Done</span>
              <CheckCircle2 className="h-4 w-4" />
            </div>
            <div className="mt-3 text-3xl font-bold">{summary.done}</div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-card p-4 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-heading text-lg font-bold">Upload File</h2>
              </div>
              <span className="text-xs text-muted-foreground">CSV / Excel</span>
            </div>

            <div className="rounded-md border border-dashed border-border p-2 text-sm">
              <label className="flex cursor-pointer flex-col items-center justify-center gap-1 py-4 text-center">
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-medium">{uploading ? "Mengupload..." : "Pilih File"}</span>
                <input type="file" accept=".csv,.xls,.xlsx" className="hidden" onChange={handleUpload} />
              </label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="serial-search-input" className="text-xs">Search SN</Label>
              <div className="flex gap-2">
                <Input
                  id="serial-search-input"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Cari Serial Number"
                  className="h-9 text-sm"
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                <Button type="button" variant="outline" size="sm" onClick={handleSearch}>Cari</Button>
              </div>
            </div>

            <form onSubmit={handleSerialSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="serial-input">Input Serial Number</Label>
                <Input
                  id="serial-input"
                  value={serialNumber}
                  onChange={(event) => setSerialNumber(event.target.value)}
                  placeholder="Contoh: SN001"
                  className="h-12 text-base"
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleSerialSubmit(event);
                    }
                  }}
                />
              </div>

              {(scannerLoading || scannerActive) && (
                <div className="overflow-hidden rounded-lg border border-border bg-slate-950/80">
                  <div className="relative aspect-[4/3] min-h-[220px] w-full overflow-hidden">
                    <div
                      id="staging-camera-reader"
                      className="absolute inset-0 h-full w-full [&_video]:h-full [&_video]:w-full [&_video]:rounded-lg [&_video]:object-cover"
                    />
                    {scannerActive && (
                    <div className="pointer-events-none absolute inset-x-[12%] top-1/2 h-20 -translate-y-1/2 rounded-lg border-2 border-primary/70 shadow-[0_0_0_9999px_hsl(240_12%_6%/0.28)]">
                      <div className="absolute inset-x-0 top-1/2 h-0.5 bg-primary shadow-[0_0_10px_hsl(var(--primary))]" />
                    </div>
                    )}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" onClick={() => (scannerActive ? stopCamera() : startCamera())} disabled={scannerLoading} className="gap-2">
                  {scannerLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : scannerActive ? <CameraOff className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
                  {scannerLoading ? "Membuka kamera..." : scannerActive ? "Tutup Kamera" : "Buka Kamera"}
                </Button>
                {scannerActive && (
                  <>
                  <Button type="button" variant="outline" size="icon" onClick={toggleTorch} disabled={!scannerActive || !torchSupported} aria-label="Nyalakan flash">
                    <Flashlight className={torchEnabled ? "text-amber-500" : ""} />
                  </Button>
                  <Button type="button" variant="outline" size="icon" onClick={switchCamera} disabled={!scannerActive} aria-label="Ganti kamera">
                    <RefreshCw />
                  </Button>
                  <label htmlFor="auto-submit-scan" className="flex cursor-pointer items-center gap-2 rounded-md border border-input px-3 py-2 text-sm">
                    <Checkbox
                      id="auto-submit-scan"
                      checked={autoSubmitScan}
                      onCheckedChange={(checked) => {
                        setAutoSubmitScan(checked === true);
                        cameraAutoSubmitRef.current = checked === true;
                      }}
                    />
                    Auto submit hasil scan
                  </label>
                  </>
                )}
              </div>

              <Button type="submit" className="h-12 w-full text-base" disabled={loading}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Proses...</> : "Submit SN"}
              </Button>
            </form>
          </div>

          <div className="w-full rounded-lg border border-border bg-card">
            <div className="flex flex-col gap-3 border-b border-border px-5 py-4 md:flex-row md:items-center md:justify-between">
              <h2 className="font-heading text-xl font-bold">Daftar Serial Number</h2>
              <div className="flex items-center gap-2">
                <Label htmlFor="status-filter" className="text-xs text-muted-foreground">Status</Label>
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">Semua</option>
                  <option value="-">-</option>
                  <option value="Staging">Staging</option>
                  <option value="Done">Done</option>
                </select>
              </div>
            </div>

            {fetching ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Memuat data...
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">Tidak ada data dengan filter status ini.</div>
            ) : (
              <div className="max-h-[700px] overflow-auto">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50 text-left text-xs text-muted-foreground sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 font-medium">Product</th>
                        <th className="px-4 py-3 font-medium">Deskripsi</th>
                        <th className="px-4 py-3 font-medium">Serial Number</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((row) => (
                        <tr key={row.id} className="border-t border-border align-top">
                          <td className="px-4 py-3">{row.product_name || "-"}</td>
                          <td className="px-4 py-3">{row.description || "-"}</td>
                          <td className="px-4 py-3 font-mono text-xs">{row.serial_number}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusStyles[row.status] || statusStyles["-"]}`}>
                              {row.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
